import React, { useMemo, useState } from 'react'
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  alpha,
  lighten,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import {
  Close as CloseIcon,
  BugReport as BugReportIcon,
  Upgrade as UpgradeIcon,
  Support as SupportIcon,
  CheckCircle as CheckCircleIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material'

import { collectTicketsForSupportType, groupTicketsForDrawer } from '../../utils/ticketDrawerData'
import { constants } from '../../config'

const { graphFilters } = constants

const getTicketNonBillableReason = (ticket, { forSupportTab = false } = {}) => {
  if (!forSupportTab) return ''
  if (!ticket || ticket.supportType !== graphFilters.SUPPORT) return ''
  if (ticket.isBillable === true) return ''
  const comments = ticket.comments
  if (comments == null) return ''
  const text = String(comments).trim()
  return text
}

const getTicketDescription = (ticket) =>
  ticket?.description || ticket?.jiraDescription || ''

const getTicketTitle = (ticket, fallbackId) =>
  ticket?.subject ||
  ticket?.summary ||
  ticket?.jiraSummary ||
  ticket?.title ||
  ticket?.jiraTitle ||
  fallbackId ||
  'Ticket'

const getSupportTypeIcon = (type) => {
  if (type === graphFilters.SUPPORT) return <SupportIcon />
  if (type === graphFilters.VULNERABILITIES) return <BugReportIcon />
  if (type === graphFilters.UPGRADES) return <UpgradeIcon />
  return null
}

const getSupportTypeColor = (type, theme) => {
  const st = theme.custom.colors.supportTypes
  if (type === graphFilters.SUPPORT) return st.support
  if (type === graphFilters.VULNERABILITIES) return st.vulnerability
  if (type === graphFilters.UPGRADES) return st.upgrade
  return theme.palette.text.secondary
}

const TicketVisualizer = (componentProps) => {
  const { props: viewProps = {}, supportTypeFilter = graphFilters.SUPPORT } = componentProps
  const theme = useTheme()
  const dd = theme.custom.colors.dataDrawer
  const chipSp = theme.custom.colors.ticketVisualizer.chipSp
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {
    activeTab = '',
    graphData = {},
    projectData = {},
    filtersSelected = {},
    clientSettings = {}
  } = viewProps

  const isSupportTicketList = supportTypeFilter === graphFilters.SUPPORT
  const [listBillableMode, setListBillableMode] = useState('all')
  const listAccent = getSupportTypeColor(supportTypeFilter, theme)
  const effectiveListBillableMode = isSupportTicketList ? listBillableMode : 'all'

  const [selectedTicket, setSelectedTicket] = useState(null)

  const dialogShellAccent = useMemo(() => {
    if (!selectedTicket) return listAccent
    const c = clientSettings[selectedTicket._project]?.color
    return c || getSupportTypeColor(selectedTicket.supportType || '', theme)
  }, [selectedTicket, clientSettings, listAccent, theme])

  const tickets = useMemo(
    () =>
      collectTicketsForSupportType({
        graphData,
        activeTab,
        projectData,
        filtersSelected,
        listBillableMode: effectiveListBillableMode,
        supportTypeFilter
      }),
    [graphData, activeTab, projectData, filtersSelected, effectiveListBillableMode, supportTypeFilter]
  )

  const groupedTickets = useMemo(() => groupTicketsForDrawer(tickets), [tickets])

  const sortedTickets = useMemo(() => {
    return [...groupedTickets].sort((a, b) => {
      const pa = String(a?._project || '')
      const pb = String(b?._project || '')
      if (pa !== pb) return pa.localeCompare(pb)
      const ka = String(a?.key || a?.ticketKey || a?.id || '')
      const kb = String(b?.key || b?.ticketKey || b?.id || '')
      return ka.localeCompare(kb)
    })
  }, [groupedTickets])

  const handleCardClick = (ticket) => {
    setSelectedTicket(ticket)
  }

  const handleCloseModal = () => {
    setSelectedTicket(null)
  }

  const listBillableFilter = isSupportTicketList
    ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.25,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(listAccent, 0.35)}`,
          backgroundColor: alpha(dd.surface, 0.65),
          backgroundImage: `linear-gradient(90deg, ${alpha(listAccent, 0.1)} 0%, transparent 70%)`,
          boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}`
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='body2' sx={{ color: dd.muted }}>
            Showing{' '}
            <Typography component='span' sx={{ color: dd.text, fontWeight: 700 }}>
              {sortedTickets.length.toLocaleString()}
            </Typography>
            {' '}ticket{sortedTickets.length !== 1 ? 's' : ''} for the selected clients and date range.
          </Typography>
          <Typography variant='caption' sx={{ color: dd.muted, display: 'block', mt: 0.35, lineHeight: 1.4 }}>
            List filter only — does not change the main chart.
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size='small'
          value={listBillableMode}
          onChange={(_, next) => {
            if (next) setListBillableMode(next)
          }}
          aria-label='Ticket list billable filter'
          sx={{
            flexShrink: 0,
            alignSelf: { xs: 'stretch', sm: 'center' },
            backgroundColor: alpha('#000', 0.22),
            borderRadius: 1,
            overflow: 'hidden',
            border: `1px solid ${alpha(dd.border, 0.9)}`,
            '& .MuiToggleButton-root': {
              color: dd.muted,
              borderColor: alpha(dd.border, 0.7),
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              px: 1.25,
              flex: { xs: 1, sm: '0 0 auto' }
            },
            '& .MuiToggleButton-root.Mui-selected': {
              color: dd.text,
              backgroundColor: alpha(listAccent, 0.28)
            },
            '& .MuiToggleButton-root.Mui-selected:hover': {
              backgroundColor: alpha(listAccent, 0.36)
            }
          }}
        >
          <ToggleButton value='all'>All</ToggleButton>
          <ToggleButton value='billable'>Billable</ToggleButton>
          <ToggleButton value='nonbillable'>Non-billable</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      )
    : (
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(listAccent, 0.35)}`,
          backgroundColor: alpha(dd.surface, 0.65),
          backgroundImage: `linear-gradient(90deg, ${alpha(listAccent, 0.1)} 0%, transparent 70%)`,
          boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}`
        }}
      >
        <Typography variant='body2' sx={{ color: dd.muted }}>
          Showing{' '}
          <Typography component='span' sx={{ color: dd.text, fontWeight: 700 }}>
            {sortedTickets.length.toLocaleString()}
          </Typography>
          {' '}ticket{sortedTickets.length !== 1 ? 's' : ''} for the selected clients and date range.
        </Typography>
      </Box>
      )

  if (sortedTickets.length === 0) {
    return (
      <Box>
        {listBillableFilter}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 220,
            p: 4,
            borderRadius: 2,
            border: `1px dashed ${alpha(listAccent, 0.55)}`,
            backgroundColor: alpha(dd.background, 0.9),
            backgroundImage: `linear-gradient(165deg, ${alpha(listAccent, 0.08)} 0%, transparent 55%)`
          }}
        >
          <Typography variant='body1' sx={{ color: dd.text, mb: 1, fontWeight: 600 }}>
            No tickets found
          </Typography>
          <Typography variant='caption' sx={{ color: dd.muted, textAlign: 'center' }}>
            {isSupportTicketList && listBillableMode !== 'all'
              ? 'Try a different billable filter or adjust the main date/client filters.'
              : 'Adjust filters or date range to see tickets.'}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {listBillableFilter}

      <Grid container spacing={2}>
        {sortedTickets.map((ticket, idx) => {
          const ticketKey = ticket?.key || ticket?.ticketKey || ticket?.id || ticket?.zendeskID || `ticket-${idx}`
          const uniqueKey = `t-${ticket.zendeskID ?? ticket.id ?? ticketKey}-${ticket._project ?? ''}-${idx}`
          const appearances = ticket._appearances || []
          const jiraAttached = ticket._jiraAttached || []

          const fallbackId = ticket?.zendeskID != null ? `Zendesk #${ticket.zendeskID}` : (ticket?.id != null && ticket.id !== '' ? String(ticket.id) : null) || ticket?.key || ticket?.ticketKey
          const title = getTicketTitle(ticket, fallbackId)
          const description = getTicketDescription(ticket)
          const isBillable = ticket?.isBillable === true
          const totalSP = appearances.length
            ? appearances.reduce((sum, a) => sum + (a.revisedSP || 0), 0)
            : Number(ticket?.revisedSP || ticket?.storyPointsActual || 0) || 0
          const revisedSP = totalSP
          const supportType = ticket?.supportType || ''
          const jiraRef = ticket?.jiraTicket ? String(ticket.jiraTicket).split(',').map(s => s.trim()).filter(Boolean) : []
          const jiraTickets = jiraAttached.length > 0
            ? jiraAttached
            : Array.isArray(ticket?.jiraTickets) ? ticket.jiraTickets : Array.isArray(ticket?.jiraKeys) ? ticket.jiraKeys : Array.isArray(ticket?.jiraIssues) ? ticket.jiraIssues : jiraRef
          const startDate = ticket?.startDate || ticket?.zdRequestDate ? (ticket.startDate || (ticket.zdRequestDate && ticket.zdRequestDate.slice(0, 10))) : ''
          const endDate = ticket?.endDate || ticket?.zdSolvedDate ? (ticket.endDate || (ticket.zdSolvedDate && ticket.zdSolvedDate.slice(0, 10))) : ''
          const formatDate = (d) => {
            if (!d) return '—'
            const s = String(d).slice(0, 10)
            if (!s) return '—'
            const [y, m, day] = s.split('-')
            if (m && day) return `${m}/${day}/${y}`
            return s
          }

          const typeColor = getSupportTypeColor(supportType, theme)
          const projectName = ticket._project
          const clientAccent = projectName && clientSettings?.[projectName]?.color
          const cardAccent = clientAccent || typeColor
          const TypeIcon = getSupportTypeIcon(supportType)
          const issueActive = ticket._issueTypeActiveInChart !== false

          return (
            <Grid item xs={12} sm={6} md={4} key={uniqueKey}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: dd.surface,
                  border: `1px solid ${issueActive ? dd.border : alpha(dd.muted, 0.45)}`,
                  borderLeft: `4px solid ${issueActive ? cardAccent : alpha(dd.muted, 0.4)}`,
                  borderRadius: 2,
                  opacity: issueActive ? 1 : 0.82,
                  filter: issueActive ? 'none' : 'grayscale(0.2)',
                  boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}, 0 8px 24px ${alpha('#000', 0.35)}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: issueActive
                      ? `inset 0 1px 0 ${alpha('#fff', 0.06)}, 0 12px 28px ${alpha('#000', 0.45)}`
                      : `inset 0 1px 0 ${alpha('#fff', 0.03)}, 0 8px 24px ${alpha('#000', 0.32)}`,
                    borderColor: issueActive ? alpha(cardAccent, 0.55) : alpha(dd.muted, 0.5),
                    transform: issueActive ? 'translateY(-2px)' : 'none'
                  }
                }}
              >
                <CardActionArea
                  onClick={() => handleCardClick(ticket)}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 0
                  }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      p: 2,
                      pr: 4.5,
                      '&:last-child': { pb: 2 }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {TypeIcon && (
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1,
                              color: cardAccent,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              backgroundColor: alpha(cardAccent, 0.18),
                              border: `1px solid ${alpha(cardAccent, 0.42)}`
                            }}
                          >
                            {TypeIcon}
                          </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant='caption'
                            sx={{
                              color: clientAccent || dd.muted,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                              display: 'block'
                            }}
                          >
                            {ticket._project}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Typography
                      variant='subtitle1'
                      sx={{
                        fontWeight: 700,
                        color: issueActive ? dd.text : alpha(dd.text, 0.75),
                        mb: 1.5,
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {title}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                      <Chip
                        size='small'
                        label={isBillable ? 'Billable' : 'Non-billable'}
                        sx={{
                          backgroundColor: isBillable
                            ? alpha(theme.palette.success.main, 0.2)
                            : alpha(theme.palette.error.main, 0.2),
                          color: isBillable
                            ? theme.palette.success.light
                            : theme.palette.error.light,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                      {revisedSP > 0 && (
                        <Chip
                          size='small'
                          label={`${revisedSP} SP`}
                          sx={{
                            backgroundColor: alpha(clientAccent || chipSp, 0.2),
                            color: lighten(clientAccent || chipSp, 0.12),
                            border: `1px solid ${alpha(clientAccent || chipSp, 0.38)}`,
                            fontWeight: 700,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                      {jiraTickets.length > 0 && (
                        <Chip
                          size='small'
                          label={`${jiraTickets.length} Jira`}
                          sx={{
                            backgroundColor: alpha(cardAccent, 0.22),
                            color: lighten(cardAccent, 0.15),
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mb: 1.5,
                        fontSize: '0.75rem',
                        color: dd.muted
                      }}
                    >
                      {ticket?.zendeskID != null && (
                        <Typography component='span' variant='caption' sx={{ display: 'block', color: 'inherit' }}>
                          <strong>Zendesk #</strong>{ticket.zendeskID}
                        </Typography>
                      )}
                      {(startDate || endDate) && (
                        <Typography component='span' variant='caption' sx={{ display: 'block', color: 'inherit' }}>
                          <strong>Start:</strong> {formatDate(startDate)} &nbsp; <strong>End:</strong> {formatDate(endDate)}
                        </Typography>
                      )}
                      <Typography component='span' variant='caption' sx={{ display: 'block', color: 'inherit' }}>
                        <strong>Billable:</strong> {isBillable ? 'Yes' : 'No'}
                      </Typography>
                    </Box>

                    {description && (
                      <Typography
                        variant='body2'
                        sx={{
                          color: alpha(dd.muted, 0.95),
                          fontSize: '0.8rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 1
                        }}
                      >
                        {description}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
                <Tooltip
                  title={
                    issueActive
                      ? 'Included in the main chart (issue type on)'
                      : 'Hidden from the main chart (issue type filtered off)'
                  }
                  placement='left'
                >
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: alpha(issueActive ? theme.palette.success.main : dd.muted, issueActive ? 0.18 : 0.12),
                      border: `1px solid ${alpha(issueActive ? theme.palette.success.main : dd.muted, 0.45)}`,
                      cursor: 'default'
                    }}
                  >
                    {issueActive
                      ? (
                        <CheckCircleIcon sx={{ fontSize: 17, color: theme.palette.success.main }} />
                        )
                      : (
                        <VisibilityOffIcon sx={{ fontSize: 17, color: dd.muted }} />
                        )}
                  </Box>
                </Tooltip>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <Dialog
        open={Boolean(selectedTicket)}
        onClose={handleCloseModal}
        fullScreen={isMobile}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: dd.background,
            backgroundImage: `linear-gradient(180deg, ${alpha(dialogShellAccent, 0.14)} 0%, transparent 72px)`,
            border: `1px solid ${dd.border}`,
            borderTop: `3px solid ${dialogShellAccent}`,
            borderRadius: isMobile ? 0 : 2,
            boxShadow: `0 8px 32px ${alpha('#000', 0.5)}`
          }
        }}
      >
        {selectedTicket && (() => {
          const t = selectedTicket
          const modalSubject = getTicketTitle(t, t?.zendeskID ? `Zendesk #${t.zendeskID}` : '')
          const modalTitle = modalSubject || (t?.zendeskID ? `Zendesk #${t.zendeskID}` : 'Ticket details')
          const modalDesc = getTicketDescription(t)
          const modalNonBillableReason = getTicketNonBillableReason(t, {
            forSupportTab: supportTypeFilter === graphFilters.SUPPORT
          })
          const modalBillable = t?.isBillable === true
          const modalJira = t._jiraAttached?.length > 0 ? t._jiraAttached : (Array.isArray(t?.jiraTickets) ? t.jiraTickets : Array.isArray(t?.jiraKeys) ? t.jiraKeys : Array.isArray(t?.jiraIssues) ? t.jiraIssues : (t?.jiraTicket ? String(t.jiraTicket).split(',').map(s => s.trim()).filter(Boolean) : []))
          const modalStart = t?.startDate || (t?.zdRequestDate && t.zdRequestDate.slice(0, 10)) || ''
          const modalEnd = t?.endDate || (t?.zdSolvedDate && t.zdSolvedDate.slice(0, 10)) || ''
          const modalTypeColor = getSupportTypeColor(t?.supportType || '', theme)
          const modalCardAccent = clientSettings[t?._project]?.color || modalTypeColor
          const modalIssueActive = t._issueTypeActiveInChart !== false
          const formatDateModal = (d) => {
            if (!d) return '—'
            const s = String(d).slice(0, 10)
            if (!s) return '—'
            const [y, m, day] = s.split('-')
            if (m && day) return `${m}/${day}/${y}`
            return s
          }
          return (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, borderBottom: `1px solid ${dd.border}`, pb: 2 }}>
                <Typography variant='h6' sx={{ fontWeight: 700, color: dd.text, flex: 1, pr: 2 }}>
                  {modalTitle}
                </Typography>
                <IconButton onClick={handleCloseModal} size='small' sx={{ color: dd.muted, backgroundColor: alpha('#fff', 0.06), '&:hover': { backgroundColor: alpha('#fff', 0.1) } }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: 2, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Box sx={{ mb: 3, p: 1.5, borderRadius: 2, backgroundColor: alpha(dd.surface, 0.9), border: `1px solid ${dd.border}`, boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}` }}>
                  <Typography variant='caption' sx={{ color: dd.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>Details</Typography>
                  {!modalIssueActive && (
                    <Typography variant='caption' sx={{ color: dd.muted, display: 'block', mb: 1.5, lineHeight: 1.45 }}>
                      This issue type is turned off on the main chart; the drawer still lists it for reference.
                    </Typography>
                  )}
                  <Grid container spacing={2}>
                    {modalSubject && (
                      <Grid item xs={12}>
                        <Typography variant='caption' sx={{ color: dd.muted }}>Subject</Typography>
                        <Typography variant='body2' sx={{ color: dd.text, fontWeight: 500 }}>{modalSubject}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={6} sm={4}>
                      <Typography variant='caption' sx={{ color: dd.muted }}>Start date</Typography>
                      <Typography variant='body2' sx={{ color: dd.text, fontWeight: 500 }}>{formatDateModal(modalStart)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant='caption' sx={{ color: dd.muted }}>End date</Typography>
                      <Typography variant='body2' sx={{ color: dd.text, fontWeight: 500 }}>{formatDateModal(modalEnd)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant='caption' sx={{ color: dd.muted }}>Billable</Typography>
                      <Typography variant='body2' sx={{ color: dd.text, fontWeight: 500 }}>{modalBillable ? 'Yes' : 'No'}</Typography>
                    </Grid>
                  </Grid>
                </Box>
                {modalNonBillableReason && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.error.main, 0.42)}`,
                      borderLeft: `4px solid ${theme.palette.error.main}`,
                      backgroundColor: alpha(theme.palette.error.main, 0.14),
                      backgroundImage: `linear-gradient(90deg, ${alpha(theme.palette.error.main, 0.2)} 0%, transparent 72%)`,
                      boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.06)}`
                    }}
                  >
                    <Typography
                      variant='caption'
                      sx={{
                        color: theme.palette.error.light,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        display: 'block',
                        mb: 0.75
                      }}
                    >
                      Non-billable reason
                    </Typography>
                    <Typography variant='body2' sx={{ color: dd.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {modalNonBillableReason}
                    </Typography>
                  </Box>
                )}
                {modalDesc && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='caption' sx={{ color: dd.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>Description</Typography>
                    <Typography variant='body2' sx={{ color: dd.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{modalDesc}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant='caption' sx={{ color: dd.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>Linked Jira Tickets ({modalJira.length})</Typography>
                  {modalJira.length === 0
                    ? <Typography variant='body2' sx={{ color: alpha(dd.muted, 0.85), fontStyle: 'italic' }}>No Jira tickets attached</Typography>
                    : (
                      <Grid container spacing={1.5}>
                        {modalJira.map((jira, jIdx) => {
                          const jiraKey = typeof jira === 'string' ? jira : (jira?.key || jira?.jiraIssueKey || `jira-${jIdx}`)
                          const jiraTitle = typeof jira === 'object' && jira ? (jira?.summary || jira?.title || '') : ''
                          const jiraDescription = typeof jira === 'object' && jira ? (jira?.description || '') : ''
                          const jiraSP = typeof jira === 'object' && jira ? (Number(jira?.revisedSP) || 0) : 0
                          const jiraEst = typeof jira === 'object' && jira && jira.storyPointsEstimated != null && String(jira.storyPointsEstimated) !== 'null' ? Number(jira.storyPointsEstimated) : null
                          return (
                            <Grid item xs={12} key={`modal-jira-${jIdx}`}>
                              <Card sx={{ backgroundColor: alpha(modalCardAccent, 0.1), border: `1px solid ${alpha(modalCardAccent, 0.4)}`, borderLeft: `3px solid ${modalCardAccent}`, borderRadius: 2, boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}` }}>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Typography variant='subtitle2' sx={{ fontWeight: 700, color: modalCardAccent, mb: 0.5 }}>{jiraKey}</Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: (jiraTitle || jiraDescription) ? 1 : 0, fontSize: '0.85rem' }}>
                                    {jiraSP > 0 && (
                                      <Typography component='span' variant='body2' sx={{ color: clientSettings[t?._project]?.color || chipSp, fontWeight: 700 }}>
                                        {jiraSP} SP
                                      </Typography>
                                    )}
                                    {jiraEst != null && Number.isFinite(jiraEst) && (
                                      <Typography component='span' variant='body2' sx={{ color: dd.muted }}>
                                        Estimated: {jiraEst} SP
                                      </Typography>
                                    )}
                                  </Box>
                                  {jiraTitle && <Typography variant='body2' sx={{ color: dd.text, fontWeight: 600, mb: jiraDescription ? 0.5 : 0 }}>{jiraTitle}</Typography>}
                                  {jiraDescription && <Typography variant='body2' sx={{ color: dd.muted, fontSize: '0.85rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{jiraDescription}</Typography>}
                                </CardContent>
                              </Card>
                            </Grid>
                          )
                        })}
                      </Grid>
                      )}
                </Box>
              </DialogContent>
            </>
          )
        })()}
      </Dialog>
    </Box>
  )
}

export { TicketVisualizer }
